import { addRecord, getRecordByIndex, STORES, updateRecord } from './indexedDBUtils.js'

let saveQueue = Promise.resolve()

/**
 * Saves patient visit & insight data to IndexedDB with upsert logic.
 *
 * The function first maps the provided arguments into a single record via
 * {@link mappingPatientVisitInsights}, then performs an upsert against the
 * `patientVisitInsights` store keyed on `encounterId`:
 * - If a record with the same `encounterId` already exists, it is merged with
 *   the new data while preserving the existing primary key (`id`).
 * - If no matching record is found, a new record is inserted.
 *
 * This prevents duplicate entries for the same encounter across multiple calls
 * (e.g., when the user revisits the triage results screen).
 *
 * @param {Array<object>} conditions - List of diagnosed conditions, each containing
 *   at minimum `name` (string), `severity` (string), and
 *   `triage_recommendation` (string).
 * @param {Array<object>} categoriesData - Raw category data used to build a
 *   condition-to-category lookup map. Each item should have `name`, `title`,
 *   and `categories` fields.
 * @param {object} currentTrack - The current track/panel object. Used to extract
 *   `path`, which represents the patient type (e.g., "child", "adult").
 * @param {Function} sessionDetails - A getter function that returns the current
 *   session object. Expected to expose:
 *   - `id.id` (string): unique encounter identifier
 *   - `timing.start` (ISO string): session start timestamp
 *   - `timing.end` (ISO string): session end timestamp
 *   - `user.id` (string|number): authenticated user identifier
 * @param {string|null} selectedTriage - The selected triage level or protocol
 *   for this encounter. Can be null if not applicable.
 * @param {Array<string>|null} selectedConditions - List of condition names that
 *   were selected/toggled by the user. Used to set the `selectedByUser` flag
 *   on each persisted condition record. Can be null if not applicable.
 * @returns {Promise<IDBValidKey>} A promise that resolves to the IndexedDB record
 *   key of the upserted record (either the existing key on update, or the
 *   auto-generated key on insert).
 * @throws {Error} Throws if any IndexedDB operation fails.
 */
export async function savePatientVisitInsights(
  conditions,
  categoriesData,
  currentTrack,
  sessionDetails,
  selectedTriage,
  selectedConditions) {
  const data = mappingPatientVisitInsights(
    conditions,
    categoriesData,
    currentTrack,
    sessionDetails,
    selectedTriage,
    selectedConditions)

  const executeDbOp = async () => {
    try {
      if (data?.encounterId) {
        const existing = await getRecordByIndex(
          STORES.PATIENT_VISIT_INSIGHTS,
          'encounterId',
          data.encounterId
        )
        if (existing) {
          const updated = { ...existing, ...data, id: existing.id }
          await updateRecord(STORES.PATIENT_VISIT_INSIGHTS, updated)
          return existing.id
        }
      }
      return await addRecord(STORES.PATIENT_VISIT_INSIGHTS, data)
    } catch (error) {
      console.error("Failed to save PatientVisitInsights to IndexedDB: ", error)
      throw error
    }
  }

  const promise = saveQueue.then(executeDbOp, executeDbOp)
  saveQueue = promise.catch(() => {})
  return promise
}

/**
 * Maps raw triage result data into a structured patient visit insight record
 * ready to be persisted in IndexedDB.
 *
 * The returned record contains session metadata, user identifier, condition
 * details with their associated categories, patient type, and the calculated
 * duration of the visit.
 *
 * The `duration` field is computed as the difference in seconds between the
 * session's `timing.start` and `timing.end` timestamps. If either timestamp
 * is missing, the value will be `null`.
 *
 * @param {Array<object>} conditions - List of diagnosed conditions from the triage engine.
 *   Each object should have:
 *   - `name` (string): the condition identifier/key
 *   - `severity` (string): severity level (e.g., "mild", "moderate", "severe")
 *   - `triage_recommendation` (string): recommended triage action for the condition
 * @param {Array<object>} categoriesData - Raw category definitions used to build a
 *   condition-to-category lookup. Each item should have:
 *   - `name` (string): condition key
 *   - `title` (string, optional): display title used as the lookup key if present
 *   - `categories` (Array): list of category objects associated with the condition
 * @param {object} currentTrack - The active track/panel object. The `path` property is
 *   used to determine the patient type (e.g., "child", "adult", "newborn").
 * @param {Function} sessionDetails - A getter function returning the current session object.
 *   Expected shape:
 *   - `id.id` (string): unique encounter UUID
 *   - `timing.start` (string): ISO 8601 timestamp of when the session started
 *   - `timing.end` (string): ISO 8601 timestamp of when the session ended
 *   - `user.id` (string|number): authenticated user identifier
 * @param {string|null} selectedTriage - The selected triage level or protocol for
 *   this encounter. Stored as-is on the record. Can be null if not applicable.
 * @param {Array<string>|null} selectedConditions - List of condition names that
 *   were selected/toggled by the user. Used to set the `selectedByUser` flag
 *   on each condition record. Can be null if not applicable.
 * @returns {object} A single mapped record object with the following fields:
 *   - `createdAt` (string): ISO timestamp of when the record was created
 *   - `encounterId` (string): unique encounter identifier from `sessionDetails().id.id`
 *   - `patientType` (string): patient type derived from `currentTrack.path`
 *   - `duration` (number|null): duration of the visit in seconds, or null if unavailable
 *   - `userId` (string|number): user identifier from `sessionDetails().user.id`, or `""`
 *   - `triage` (string|null): the selected triage value
 *   - `conditions` (Array<object>): list of condition records, each containing:
 *     - `name` (string): condition identifier
 *     - `severity` (string): severity level
 *     - `triage_recommendation` (string): recommended triage action
 *     - `categories` (Array): associated category objects, or `[]` if none found
 *     - `selectedByUser` (boolean): whether the condition was selected/toggled by the user
 */
export function mappingPatientVisitInsights(
  conditions,
  categoriesData,
  currentTrack,
  sessionDetails,
  selectedTriage,
  selectedConditions) {
  const categoriesSet = extractCategories(categoriesData)

  const now = new Date()
  const timingData = sessionDetails()?.timing || {}
  const startTime = timingData?.start ? new Date(timingData?.start) : null
  const endTime = timingData?.end ? new Date(timingData?.end) : null
  const userDetails = sessionDetails()?.user || {}

  const timePerVisit = startTime && endTime ? Math.floor((endTime - startTime) / 1000) : null

  const selectedSet = new Set(selectedConditions || [])

  return {
    createdAt: now.toISOString(),
    encounterId: sessionDetails()?.id?.id,
    patientType: currentTrack?.path,
    duration: timePerVisit,
    userId: userDetails?.id || "",
    deviceId: userDetails?.hr_id || "",
    triage: selectedTriage,
    conditions: conditions.map(condition => {
      return {
        name: condition.name,
        severity: condition.severity,
        triage_recommendation: condition.triage_recommendation,
        categories: categoriesSet[condition.name] || [],
        selectedByUser: selectedSet.has(condition.name),
      }
    })
  }

}

/**
 * Builds a condition-to-category lookup map from the raw categories data array.
 *
 * Iterates over each condition entry in `categoriesData` and constructs an object
 * where the key is the condition's `title` (if available) or `name` (as fallback),
 * and the value is the array of categories associated with that condition.
 *
 * Conditions with an empty or missing `categories` array are skipped and will not
 * appear in the resulting map.
 *
 * This map is used by `mappingPatientVisitInsights` to efficiently look up the
 * category data for each diagnosed condition by name.
 *
 * @param {Array<object>} categoriesData - Array of condition objects to process.
 *   Each object should have:
 *   - `name` (string): the condition's unique key, used as fallback lookup key
 *   - `title` (string, optional): preferred display key; used over `name` if present
 *   - `categories` (Array): list of category items associated with the condition;
 *     entries with an empty array are excluded from the result
 * @returns {object} A flat key-value map where:
 *   - key: condition title or name (string)
 *   - value: array of category objects for that condition
 *
 * @example
 * const data = [
 *   { name: 'malaria', title: 'Malaria', categories: [{ label: 'Fever' }] },
 *   { name: 'anemia', title: '', categories: [] }, // skipped - empty categories
 * ]
 * extractCategories(data)
 * // => { 'Malaria': [{ label: 'Fever' }] }
 */
const extractCategories = (categoriesData) => {
  const result = {}

  categoriesData.forEach(condition => {
    const diseaseKey = condition.name

    if (condition.categories && condition.categories.length > 0) {
      result[condition.title || diseaseKey] = condition.categories
    }
  })

  return result;
}